import React, { useState, useEffect } from 'react';
import { observer } from 'mobx-react';
import { Typography, Box, Paper, Divider, TextField, FormControlLabel, Switch, Grid, Select, MenuItem } from '@mui/material';
import SettingsEthernetIcon from '@mui/icons-material/SettingsEthernet';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import KeyIcon from '@mui/icons-material/Key';
import PeopleIcon from '@mui/icons-material/People';
import LanguageIcon from '@mui/icons-material/Language';
import PublicIcon from '@mui/icons-material/Public';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import authStore from '../stores/AuthStore';
import apiHandler from '../handlers/ApiHandler';
import userPreferencesStore from '../stores/UserPreferencesStore';

const SettingsPage = observer(() => {
  // API Settings
  const [apiUrl, setApiUrl] = useState(process.env.REACT_APP_API || '');
  const [loading, setLoading] = useState(true);
  const [userId, setUserId] = useState(authStore.userId);
  const [token, setToken] = useState('');
  const [debugMode, setDebugMode] = useState(false);
  const [connectionSpeed, setConnectionSpeed] = useState('Not Available');
  const [currentIP, setCurrentIP] = useState('Not Available');
  const [apiIP, setApiIP] = useState('Not Available');

  // User Details
  const [username, setUsername] = useState(userPreferencesStore.username);
  const [displayName, setDisplayName] = useState(userPreferencesStore.displayName);
  const [email, setEmail] = useState(userPreferencesStore.email);

  // Locale Preferences
  const [selectedLanguage, setSelectedLanguage] = useState(userPreferencesStore.selectedLanguage);
  const [selectedTimeZone, setSelectedTimeZone] = useState(userPreferencesStore.selectedTimeZone);
  const [selectedDateFormat, setSelectedDateFormat] = useState(userPreferencesStore.selectedDateFormat);

  const handleLanguageChange = (event) => {
    const language = event.target.value || '';
    setSelectedLanguage(language);
    userPreferencesStore.updatePreferences(language, selectedTimeZone, selectedDateFormat);
  };

  const handleTimeZoneChange = (event) => {
    const timeZone = event.target.value || '';
    setSelectedTimeZone(timeZone);
    userPreferencesStore.updatePreferences(selectedLanguage, timeZone, selectedDateFormat);
  };

  const handleDateFormatChange = (event) => {
    const dateFormat = event.target.value || '';
    setSelectedDateFormat(dateFormat);
    userPreferencesStore.updatePreferences(selectedLanguage, selectedTimeZone, dateFormat);
  };
  
  useEffect(() => {
    setUserId(authStore.userId);
  }, [authStore.userId]);

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const userData = await apiHandler.getUserData(userId);
        setUsername(userData.username);
        setDisplayName(userData.displayName);
        setEmail(userData.email);
        setSelectedLanguage(userData.selectedLanguage);
        setSelectedTimeZone(userData.selectedTimeZone);
        setSelectedDateFormat(userData.selectedDateFormat);
      } catch (error) {
        console.error('Failed to fetch user data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, [userId]);

  useEffect(() => {
    setApiUrl(authStore.apiUrl); // Replace with actual logic to get API URL from authStore
    setToken(authStore.token); // Replace with actual logic to get token from authStore
    setDebugMode(authStore.debugMode); // Replace with actual logic to get debug mode from authStore
    setSelectedLanguage(userPreferencesStore.selectedLanguage);
    setSelectedTimeZone(userPreferencesStore.selectedTimeZone);
    setSelectedDateFormat(userPreferencesStore.selectedDateFormat);
    setUsername(userPreferencesStore.username);
    setDisplayName(userPreferencesStore.displayName);
    setEmail(userPreferencesStore.email);

    const fetchData = async () => {
      try {
        const pingResponse = await apiHandler.ping(authStore.apiUrl);
        const currentTime = new Date().getTime();
        setConnectionSpeed(`${currentTime - pingResponse.startTime} ms`);
      } catch (error) {
        setConnectionSpeed('Unavailable');
      }

      try {
        setCurrentIP(await apiHandler.getCurrentIP());
        setApiIP(await apiHandler.getApiIP());
      } catch (error) {
        setCurrentIP('Unavailable');
        setApiIP('Unavailable');
      }
    };

    fetchData();
  }, [authStore.apiUrl, authStore.token, authStore.debugMode, userPreferencesStore.selectedLanguage, userPreferencesStore.selectedTimeZone, userPreferencesStore.selectedDateFormat, userPreferencesStore.username, userPreferencesStore.displayName, userPreferencesStore.email]);

  return (

    loading ? (
        <div>Loading...</div>
      ) : (
        <>
      <Paper elevation={3} sx={{ p: 2, m: 2 }}>
        <Typography variant="h5" gutterBottom color="primary">
          Account
        </Typography>
        <Divider sx={{ mb: 3 }} />
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <AccountCircleIcon sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            Username
          </Typography>
        </Box>
        <TextField
          fullWidth
          variant="outlined"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          sx={{ mb: 2 }}
        />
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <PeopleIcon sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            Display Name
          </Typography>
        </Box>
        <TextField
          fullWidth
          variant="outlined"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          sx={{ mb: 2 }}
        />
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <KeyIcon sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            Email
          </Typography>
        </Box>
        <TextField
          fullWidth
          variant="outlined"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          sx={{ mb: 2 }}
        />
      </Paper>

      <Paper elevation={3} sx={{ p: 2, m: 2 }}>
        <Typography variant="h5" gutterBottom color="primary">
          Locale
        </Typography>
        <Divider sx={{ mb: 3 }} />

        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <LanguageIcon sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            Language
          </Typography>
          <Select
            value={selectedLanguage}
            onChange={handleLanguageChange}
            sx={{ marginLeft: 'auto', minWidth: 120 }}
          >
            <MenuItem value="English">English</MenuItem>
            <MenuItem value="Spanish">Spanish</MenuItem>
            <MenuItem value="French">French</MenuItem>
          </Select>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <PublicIcon sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            Time Zone
          </Typography>
          <Select
            value={selectedTimeZone}
            onChange={handleTimeZoneChange}
            sx={{ marginLeft: 'auto', minWidth: 120 }}
          >
            <MenuItem value="UTC">UTC</MenuItem>
            <MenuItem value="GMT">GMT</MenuItem>
            <MenuItem value="EST">EST</MenuItem>
          </Select>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <CalendarTodayIcon sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            Date Format
          </Typography>
          <Select
            value={selectedDateFormat}
            onChange={handleDateFormatChange}
            sx={{ marginLeft: 'auto', minWidth: 120 }}
          >
            <MenuItem value="MM/DD/YYYY">MM/DD/YYYY</MenuItem>
            <MenuItem value="DD/MM/YYYY">DD/MM/YYYY</MenuItem>
            <MenuItem value="YYYY-MM-DD">YYYY-MM-DD</MenuItem>
          </Select>
        </Box>
      </Paper>
      <Paper elevation={3} sx={{ p: 2, m: 2 }}>
        <Typography variant="h5" gutterBottom color="primary">
          API
        </Typography>
        <Divider sx={{ mb: 3 }} />

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
          <TextField
              label="API URL"
              variant="outlined"
              fullWidth
              value={apiUrl}
              onChange={(e) => setApiUrl(e.target.value)}
              sx={{ mb: 2 }}
            />
            <TextField
              label="Token"
              variant="outlined"
              fullWidth
              value={token}
              onChange={(e) => setToken(e.target.value)}
              sx={{ mb: 2 }}
            />
            <FormControlLabel
              control={<Switch checked={debugMode} onChange={(e) => setDebugMode(e.target.checked)} />}
              label="Debug Mode"
              labelPlacement="start"
              sx={{ mb: 2, marginLeft: 0 }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <SettingsEthernetIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="body1">Connection Speed: {connectionSpeed}</Typography>
            </Box>
            <Typography variant="body1" sx={{ mb: 2 }}>Current IP: {currentIP}</Typography>
            <Typography variant="body1">API: {apiIP}</Typography>
          </Grid>
        </Grid>
      </Paper>
      </>
      )
  );
});

export default SettingsPage;
